import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { adminApiFetch } from '../../../utils/adminApi';
import { API_URLS } from '../../../config/api';

const headerMap = {
  'Müşteri Ünvanı': 'title',
  'Müşteri Kodu': 'business_code',
  'Adres': 'address',
  'Vergi Dairesi': 'tax_office',
  'Vergi No': 'tax_number',
  'Kategori': 'category',
  'E-Posta': 'email',
  'Cep Telefonu': 'phone',
  'Yetkili Kişi Adı': 'contact_name',
  'Bakiyesi': 'balance',
  'Para Birimi': 'currency'
};

const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [message, setMessage] = useState('');

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const res = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getBusinesses`);
      const data = await res.json();
      if (data?.status === 'success' && Array.isArray(data.data)) {
        setBusinesses(data.data);
      } else {
        setMessage('İşletmeler alınamadı');
      }
    } catch (error) {
      console.error('getBusinesses error', error);
      setMessage('İşletmeler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const parseFile = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    const mapped = rows
      .map((row) => {
        const mappedRow = {};
        Object.entries(headerMap).forEach(([header, key]) => {
          if (row[header] !== undefined && row[header] !== null) {
            mappedRow[key] = String(row[header]).trim();
          }
        });
        return mappedRow;
      })
      .filter((row) => row.title && row.title.trim() !== '');
    setParsedRows(mapped);
    setMessage(`${mapped.length} satır hazırlandı. Kaydetmek için "İçe Aktar" tıkla.`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await parseFile(file);
    } catch (error) {
      console.error('parse error', error);
      setMessage('Dosya okunamadı. Şablonu kontrol edin.');
    }
  };

  const handleImport = async () => {
    if (!parsedRows.length) {
      setMessage('İçe aktarılacak veri yok.');
      return;
    }
    setImporting(true);
    try {
      const res = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=bulkUpsertBusinesses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedRows)
      });
      const result = await res.json();
      if (result?.status === 'success') {
        setMessage(`İçe aktarma tamamlandı. Eklendi: ${result.inserted}, Güncellendi: ${result.updated}`);
        setParsedRows([]);
        await fetchBusinesses();
      } else {
        setMessage(result?.message || 'İçe aktarma başarısız.');
      }
    } catch (error) {
      console.error('import error', error);
      setMessage('İçe aktarma sırasında hata oluştu.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl">İşletmeler</CardTitle>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">isletmeler.xlsx şablonuyla içe aktarım yapabilirsiniz.</p>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
          <div className="flex flex-col gap-3">
            <Input 
              type="file" 
              accept=".xlsx,.xls" 
              onChange={handleFileChange}
              className="w-full text-xs sm:text-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button 
                onClick={handleImport} 
                disabled={importing || parsedRows.length === 0}
                className="text-xs sm:text-sm h-8 sm:h-10 px-3 sm:px-4"
              >
                {importing ? 'Aktarılıyor...' : 'İçe Aktar'}
              </Button>
              {parsedRows.length > 0 && (
                <Badge variant="secondary" className="text-xs">{parsedRows.length} satır hazır</Badge>
              )}
            </div>
          </div>
          {message && <div className="text-xs sm:text-sm text-gray-600">{message}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl">İşletme Listesi</CardTitle>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Toplam {businesses.length} kayıt</p>
        </CardHeader>
        <CardContent className="p-2 sm:p-6">
          {loading ? (
            <div className="text-sm sm:text-base py-4">Yükleniyor...</div>
          ) : businesses.length === 0 ? (
            <div className="text-sm sm:text-base text-gray-500 py-4 text-center">Kayıt bulunamadı.</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-4">Ünvan</th>
                      <th className="py-2 pr-4">Kod</th>
                      <th className="py-2 pr-4">Kategori</th>
                      <th className="py-2 pr-4">Yetkili</th>
                      <th className="py-2 pr-4">E-Posta</th>
                      <th className="py-2 pr-4">Telefon</th>
                      <th className="py-2 pr-4">Vergi Dairesi / No</th>
                      <th className="py-2 pr-4">Bakiye</th>
                      <th className="py-2 pr-4">Para Birimi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map((b) => (
                      <tr key={b.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">{b.title}</td>
                        <td className="py-2 pr-4">{b.business_code || '—'}</td>
                        <td className="py-2 pr-4">{b.category || '—'}</td>
                        <td className="py-2 pr-4">{b.contact_name || '—'}</td>
                        <td className="py-2 pr-4">{b.email || '—'}</td>
                        <td className="py-2 pr-4">{b.phone || '—'}</td>
                        <td className="py-2 pr-4">
                          {b.tax_office || '—'} {b.tax_number ? ` / ${b.tax_number}` : ''}
                        </td>
                        <td className="py-2 pr-4">{b.balance !== null ? b.balance : '—'}</td>
                        <td className="py-2 pr-4">{b.currency || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {businesses.map((b) => (
                  <Card key={b.id} className="overflow-hidden">
                    <CardContent className="p-4 space-y-2">
                      <div>
                        <div className="font-semibold text-sm truncate">{b.title}</div>
                        {b.business_code && (
                          <div className="text-xs text-gray-500 mt-0.5">Kod: {b.business_code}</div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        {b.category && (
                          <div>
                            <span className="text-gray-500">Kategori:</span>{' '}
                            <span className="font-medium">{b.category}</span>
                          </div>
                        )}
                        {b.contact_name && (
                          <div>
                            <span className="text-gray-500">Yetkili:</span>{' '}
                            <span className="font-medium">{b.contact_name}</span>
                          </div>
                        )}
                        {b.email && (
                          <div>
                            <span className="text-gray-500">E-Posta:</span>{' '}
                            <span className="font-medium break-all">{b.email}</span>
                          </div>
                        )}
                        {b.phone && (
                          <div>
                            <span className="text-gray-500">Telefon:</span>{' '}
                            <span className="font-medium">{b.phone}</span>
                          </div>
                        )}
                        {(b.tax_office || b.tax_number) && (
                          <div>
                            <span className="text-gray-500">Vergi:</span>{' '}
                            <span className="font-medium">
                              {b.tax_office || '—'} {b.tax_number ? ` / ${b.tax_number}` : ''}
                            </span>
                          </div>
                        )}
                        {b.balance !== null && (
                          <div>
                            <span className="text-gray-500">Bakiye:</span>{' '}
                            <span className="font-medium">{b.balance} {b.currency || ''}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBusinesses;

